import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { GoogleMap, Polygon } from '@react-google-maps/api';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudDownload as CloudDownloadIcon,
  UploadFile as UploadFileIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { createSector } from '../../redux/slices/sectorsSlice';
import { calculateBounds, calculateCentroid } from '../../utils/sectorGeometry';
import {
  DEFAULT_SIMPLIFY_KEY,
  SIMPLIFY_LEVELS,
  arcGisLayerInfo,
  buildImportCandidates,
  candidateBlockReason,
  guessPropertyKeys,
  normalizeGeoJsonUrl,
  readFeatures,
  readServiceLayers,
  searchPlaces,
  toleranceForLevel,
  unionGeometries
} from '../../utils/geoJsonSectors';

// Beispiel-URL der Stadt Kassel: ein offener ArcGIS-Dienst, an dem sich die
// Form einer solchen Adresse ablesen laesst.
const EXAMPLE_URL = 'https://geoportal.kassel.de/arcgis/rest/services/Service_Daten/Politik_Verwaltung/MapServer/0';

// Farben fuer importierte Sektoren, damit nebeneinanderliegende Stadtteile auf
// der Karte unterscheidbar sind.
const IMPORT_COLORS = [
  '#2E7D32', '#1565C0', '#C62828', '#6A1B9A', '#EF6C00',
  '#00838F', '#AD1457', '#4E342E', '#37474F', '#9E9D24'
];

const SERVICE_AREA_COLOR = '#1565C0';
const MAP_STYLE = { width: '100%', height: '380px' };
const FALLBACK_CENTER = { lat: 51.3127, lng: 9.4797 };

/**
 * Uebernimmt Flaechen aus einer GeoJSON-Quelle.
 *
 * Zwei Betriebsarten, weil dieselbe Quelle beides hergibt: `sectors` legt aus
 * mehreren Flaechen Sektoren an, `serviceArea` reicht genau eine Flaeche (die
 * Stadt- oder Gemeindegrenze) an den Betriebsgebiet-Editor weiter.
 */
const SectorImportDialog = ({
  open,
  onClose,
  existingSectors = [],
  isMapLoaded,
  onImported,
  mode = 'sectors',
  onSelectPolygon
}) => {
  const isServiceAreaMode = mode === 'serviceArea';
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);

  const [sourceTab, setSourceTab] = useState('search');
  const [sourceKind, setSourceKind] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loadHints, setLoadHints] = useState([]);

  const [serviceUrl, setServiceUrl] = useState('');
  const [serviceLayers, setServiceLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);

  const [features, setFeatures] = useState([]);
  const [propertyKeys, setPropertyKeys] = useState([]);
  const [nameKey, setNameKey] = useState('');
  const [codeKey, setCodeKey] = useState('');
  const [simplifyKey, setSimplifyKey] = useState(DEFAULT_SIMPLIFY_KEY);

  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importError, setImportError] = useState('');

  const resetSource = () => {
    setFeatures([]);
    setPropertyKeys([]);
    setNameKey('');
    setCodeKey('');
    setRows([]);
    setLoadHints([]);
    setImportError('');
  };

  const handleClose = () => {
    if (importing) return;
    onClose();
  };

  const acceptFeatures = (parsedFeatures, label, kind) => {
    const { keys, nameKey: guessedName, codeKey: guessedCode } = guessPropertyKeys(parsedFeatures);
    setFeatures(parsedFeatures);
    setPropertyKeys(keys);
    setNameKey(guessedName);
    setCodeKey(guessedCode);
    setSourceLabel(label);
    setSourceKind(kind);
    setImportError('');
  };

  // Nominatim erlaubt ausdruecklich kein Type-ahead — eine Abfrage pro Klick.
  const handleSearch = async () => {
    setLoading(true);
    setLoadError('');
    resetSource();
    setServiceUrl('');
    setServiceLayers([]);
    setActiveLayerId(null);
    try {
      const found = await searchPlaces(searchQuery, async (url) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Die Suche antwortete mit ${response.status}.`);
        return response.json();
      });
      if (!found.length) throw new Error('Keine Treffer. Suchen Sie in der Form „Stadtteil, Stadt".');
      acceptFeatures(found, 'OpenStreetMap', 'search');
    } catch (error) {
      setLoadError(error instanceof TypeError
        ? 'Die Suche ist nicht erreichbar. Bitte später erneut versuchen oder eine Datei verwenden.'
        : error.message);
    } finally {
      setLoading(false);
    }
  };

  // Die uebrigen Ebenen desselben Dienstes nachladen — reine Bequemlichkeit,
  // ein Fehlschlag bleibt deshalb stumm.
  const loadServiceLayers = async (layerInfo) => {
    setServiceUrl(layerInfo.serviceUrl);
    setActiveLayerId(layerInfo.layerId);
    try {
      const response = await fetch(`${layerInfo.serviceUrl}?f=json`);
      if (!response.ok) return;
      setServiceLayers(readServiceLayers(await response.json()));
    } catch (error) {
      setServiceLayers([]);
    }
  };

  const loadFromUrl = async (rawUrl) => {
    const { url, hints } = normalizeGeoJsonUrl(rawUrl);
    if (!url) return;
    setLoading(true);
    setLoadError('');
    resetSource();
    setLoadHints(hints);
    setSourceUrl(url);
    const layerInfo = arcGisLayerInfo(url);
    if (!layerInfo) {
      setServiceUrl('');
      setServiceLayers([]);
      setActiveLayerId(null);
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Der Server antwortete mit ${response.status} ${response.statusText}.`);
      }
      const parsedFeatures = readFeatures(await response.json());
      if (!parsedFeatures.length) throw new Error('Die Antwort enthält keine Flächen.');
      let label = url;
      try {
        label = new URL(url).hostname;
      } catch (error) {
        label = 'GeoJSON-Quelle';
      }
      acceptFeatures(parsedFeatures, label, 'url');
      if (layerInfo) loadServiceLayers(layerInfo);
    } catch (error) {
      // Ein fehlgeschlagenes fetch() ohne Status ist praktisch immer CORS: der
      // fremde Server erlaubt den Zugriff aus dem Browser nicht. Das laesst
      // sich nicht umgehen, nur umschiffen — deshalb der Hinweis auf die Datei.
      const isNetwork = error instanceof TypeError;
      setLoadError(isNetwork
        ? 'Der Server hat den Zugriff aus dem Browser abgelehnt (CORS) oder ist nicht erreichbar. Laden Sie die Datei im Portal der Stadt herunter und wählen Sie sie unten als Datei aus.'
        : error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLoading(true);
    setLoadError('');
    resetSource();
    setServiceUrl('');
    setServiceLayers([]);
    setActiveLayerId(null);
    try {
      const parsedFeatures = readFeatures(JSON.parse(await file.text()));
      if (!parsedFeatures.length) throw new Error('Die Datei enthält keine Flächen.');
      acceptFeatures(parsedFeatures, file.name, 'file');
    } catch (error) {
      setLoadError(error instanceof SyntaxError
        ? 'Die Datei ist kein gültiges JSON. Erwartet wird eine GeoJSON-Datei (.geojson oder .json).'
        : error.message);
    } finally {
      setLoading(false);
    }
  };

  const candidates = useMemo(
    () => buildImportCandidates(features, {
      nameKey,
      codeKey,
      toleranceMeters: toleranceForLevel(simplifyKey)
    }),
    [features, nameKey, codeKey, simplifyKey]
  );

  // Namen und Nummern sind nachtraeglich editierbar; sobald sich die
  // Spaltenzuordnung oder die Vereinfachung aendert, gilt wieder der Vorschlag
  // aus den Daten.
  useEffect(() => {
    const highestExisting = existingSectors.reduce((max, sector) => {
      const numeric = parseInt(String(sector.code || '').replace(/\D/g, ''), 10);
      return Number.isFinite(numeric) ? Math.max(max, numeric) : max;
    }, 0);
    let nextFree = highestExisting;
    setRows(candidates.map(candidate => {
      if (!candidate.code) nextFree += 1;
      return {
        key: candidate.key,
        selected: !isServiceAreaMode && !candidateBlockReason(candidate),
        name: candidate.name,
        code: candidate.code || String(nextFree)
      };
    }));
  }, [candidates, isServiceAreaMode, existingSectors]);

  const candidateByKey = useMemo(() => {
    const map = {};
    candidates.forEach(candidate => { map[candidate.key] = candidate; });
    return map;
  }, [candidates]);

  const existingCodes = useMemo(() => new Set(
    existingSectors
      .map(sector => (sector.code || '').trim().toLowerCase())
      .filter(Boolean)
  ), [existingSectors]);

  const rowErrors = useMemo(() => {
    const errors = {};
    const seen = {};
    // Das Betriebsgebiet hat weder Name noch Nummer — dort gibt es nichts zu
    // kollidieren.
    if (isServiceAreaMode) return errors;
    rows.filter(row => row.selected).forEach(row => {
      const code = (row.code || '').trim().toLowerCase();
      if (!row.name.trim()) errors[row.key] = 'Name fehlt';
      else if (!code) errors[row.key] = 'Sektornummer fehlt';
      else if (existingCodes.has(code)) errors[row.key] = 'Nummer ist bereits vergeben';
      else if (seen[code]) errors[row.key] = 'Nummer doppelt in der Auswahl';
      if (code) seen[code] = true;
    });
    return errors;
  }, [rows, existingCodes, isServiceAreaMode]);

  const selectedRows = useMemo(() => rows.filter(row => row.selected), [rows]);
  const importableCount = candidates.filter(candidate => !candidateBlockReason(candidate)).length;
  const hasErrors = Object.keys(rowErrors).length > 0;

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...patch } : row)));
  };

  const toggleAll = (checked) => {
    setRows(prev => prev.map(row => ({
      ...row,
      selected: checked ? !candidateBlockReason(candidateByKey[row.key]) : false
    })));
  };

  /**
   * Das Betriebsgebiet ist EINE Flaeche. Sind mehrere Gemeinden oder Stadtteile
   * gewaehlt, werden ihre Rohgeometrien vereinigt und erst das Ergebnis einmal
   * vereinfacht — so bleiben die gemeinsamen Grenzen nahtlos.
   */
  const serviceAreaCandidate = useMemo(() => {
    if (!isServiceAreaMode || !selectedRows.length) return null;
    const geometries = selectedRows
      .map(row => candidateByKey[row.key]?.geometry)
      .filter(Boolean);
    try {
      const merged = unionGeometries(geometries);
      if (!merged) return null;
      const [candidate] = buildImportCandidates(
        [{ type: 'Feature', properties: {}, geometry: merged }],
        { toleranceMeters: toleranceForLevel(simplifyKey) }
      );
      return candidate;
    } catch (error) {
      return { unionError: error.message, points: [] };
    }
  }, [isServiceAreaMode, selectedRows, candidateByKey, simplifyKey]);

  const serviceAreaBlockReason = serviceAreaCandidate && !serviceAreaCandidate.unionError
    ? candidateBlockReason(serviceAreaCandidate)
    : null;

  // Vorschau: die Karte folgt der Auswahl, damit man vor dem Anlegen sieht, was
  // man bekommt — Namenssuche allein trifft schon mal die falsche Fläche.
  const previewPolygons = useMemo(() => {
    if (isServiceAreaMode) {
      return serviceAreaCandidate && serviceAreaCandidate.points.length >= 3 ? [serviceAreaCandidate] : [];
    }
    return selectedRows
      .map(row => candidateByKey[row.key])
      .filter(candidate => candidate && candidate.points.length >= 3);
  }, [isServiceAreaMode, serviceAreaCandidate, selectedRows, candidateByKey]);

  // Absichtlich an der Auswahl-Signatur haengend und nicht an previewPolygons:
  // das Array wird bei jedem Tastendruck neu gebaut, die Karte soll aber nur
  // beim Aendern der Auswahl neu ausrichten.
  const previewSignature = `${mode}:${simplifyKey}:${selectedRows.map(row => row.key).join('|')}`;

  const fitPreview = (map) => {
    const target = map || mapRef.current;
    if (!target || !previewPolygons.length || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    previewPolygons.forEach(candidate => candidate.points.forEach(point => bounds.extend(point)));
    target.fitBounds(bounds);
  };

  useEffect(() => {
    fitPreview();
  }, [previewSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const colorForIndex = (index) => (isServiceAreaMode
    ? SERVICE_AREA_COLOR
    : IMPORT_COLORS[(existingSectors.length + index) % IMPORT_COLORS.length]);

  const handleTakeServiceArea = () => {
    if (!serviceAreaCandidate || serviceAreaCandidate.unionError || serviceAreaBlockReason) return;
    onSelectPolygon(serviceAreaCandidate.points);
    onClose();
  };

  const handleImport = async () => {
    setImporting(true);
    setImportError('');
    setImportProgress(0);

    const baseSortOrder = existingSectors.reduce(
      (max, sector) => Math.max(max, Number(sector.sortOrder) || 0),
      0
    );
    const failed = [];
    let created = 0;

    for (let index = 0; index < selectedRows.length; index += 1) {
      const row = selectedRows[index];
      const candidate = candidateByKey[row.key];
      if (!candidate) continue;
      const polygon = candidate.points;
      // eslint-disable-next-line no-await-in-loop
      const result = await dispatch(createSector({
        name: row.name.trim(),
        code: row.code.trim(),
        description: sourceLabel ? `Importiert aus ${sourceLabel}` : '',
        color: colorForIndex(index),
        active: true,
        dispatchFromQueueEnd: false,
        sortOrder: baseSortOrder + index + 1,
        priority: 100,
        polygon,
        bounds: calculateBounds(polygon),
        centroid: calculateCentroid(polygon)
      }));
      if (result.error) failed.push(`${row.name}: ${result.payload || result.error.message}`);
      else created += 1;
      setImportProgress(index + 1);
    }

    setImporting(false);
    if (failed.length) {
      setImportError(`${created} von ${selectedRows.length} angelegt. Fehlgeschlagen — ${failed.join('; ')}`);
      onImported(created);
      return;
    }
    onImported(created);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isServiceAreaMode ? 'Grenzen als Betriebsgebiet übernehmen' : 'Grenzen als Sektoren übernehmen'}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isServiceAreaMode
            ? 'Als Betriebsgebiet eignet sich meist die amtliche Stadt- oder Gemeindegrenze; viele Geoportale führen sie als eigene Ebene (etwa „Gemeindegrenzen"). Mehrere Flächen können ausgewählt werden — sie werden zu einem Außenriss vereinigt, innenliegende Grenzen verschwinden. Das Ergebnis landet im Betriebsgebiet-Editor und wird erst dort gespeichert.'
            : 'Stadtteilgrenzen müssen nicht abgezeichnet werden — die meisten Städte veröffentlichen sie als offene Daten. Geladene Flächen werden vereinfacht, damit sie sich im Karteneditor noch bearbeiten lassen, und können nach dem Import wie jeder andere Sektor angepasst werden.'}
        </Typography>

        <Tabs
          value={sourceTab}
          onChange={(event, value) => setSourceTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="search" label="Nach Namen suchen" disabled={loading || importing} />
          <Tab value="url" label="URL oder Datei" disabled={loading || importing} />
        </Tabs>

        {sourceTab === 'search' ? (
          <>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ort oder Stadtteil"
                  placeholder="Vorderer Westen, Kassel"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') handleSearch(); }}
                  disabled={loading || importing}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  onClick={handleSearch}
                  disabled={searchQuery.trim().length < 3 || loading || importing}
                >
                  Suchen
                </Button>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mb: 2 }}>
              Sucht Grenzen in OpenStreetMap — geeignet für Städte ohne eigenes Geoportal. Am besten in der Form
              „<em>Stadtteil, Stadt</em>" suchen und einzeln übernehmen. Nicht jeder Stadtteil ist dort als Fläche
              erfasst; oft gibt es nur einen Punkt, solche Treffer sind in der Liste gesperrt. Amtliche Daten aus dem
              Geoportal der Stadt sind, wo vorhanden, die genauere Quelle.
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                <Typography variant="caption">Daten © OpenStreetMap-Mitwirkende, ODbL.</Typography>
              </Box>
            </Alert>
          </>
        ) : (
        <>
        <Accordion variant="outlined" sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Woher bekomme ich die URL oder die Datei?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <Typography variant="body2">
                <strong>1. Datenquelle suchen.</strong> Im Netz nach „<em>Stadtname</em> Open Data Stadtteile" oder
                „<em>Stadtname</em> Geoportal Ortsbezirke" suchen. Gesucht ist ein Datensatz mit den Grenzen der
                Stadtteile, Ortsbezirke oder statistischen Bezirke.
              </Typography>
              <Typography variant="body2">
                <strong>2a. Adresse eines Kartendienstes (häufigster Fall).</strong> Endet die Adresse auf
                <code> /MapServer/0</code> oder <code>/FeatureServer/0</code>, genügt genau diese Adresse — die nötigen
                Parameter (<code>/query</code>, <code>f=geojson</code>, <code>outSR=4326</code>) ergänzt dieser Dialog
                selbst.
              </Typography>
              <Typography variant="body2">
                <strong>2b. WFS-Dienst.</strong> Dort lautet die Adresse
                <code> …?service=WFS&amp;version=2.0.0&amp;request=GetFeature&amp;typeName=EBENE&amp;outputFormat=application/json&amp;srsName=EPSG:4326</code>.
              </Typography>
              <Typography variant="body2">
                <strong>3. Oder als Datei.</strong> Im Open-Data-Portal die Stadtteile als <strong>GeoJSON</strong>
                &nbsp;herunterladen und unten auswählen. Liegt nur Shapefile, GML oder KML vor: in QGIS öffnen und über
                „Exportieren → Objekte speichern als…" als GeoJSON mit dem Koordinatensystem <strong>EPSG:4326</strong>
                &nbsp;speichern.
              </Typography>
              <Typography variant="body2">
                <strong>4. Wenn das Laden per Adresse fehlschlägt.</strong> Manche Server verbieten den Zugriff aus dem
                Browser. Dann die Adresse einfach im Browser aufrufen, die Antwort als Datei speichern und hier hochladen.
              </Typography>
              <Alert severity="info" icon={false}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Beispiel — Ortsbezirke der Stadt Kassel:
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{EXAMPLE_URL}</Typography>
                <Box sx={{ mt: 1 }}>
                  <Link component="button" variant="body2" type="button" onClick={() => setSourceUrl(EXAMPLE_URL)}>
                    Beispiel-Adresse übernehmen
                  </Link>
                </Box>
              </Alert>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <TextField
              fullWidth
              size="small"
              label="URL des Datensatzes"
              placeholder="https://…/MapServer/0"
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              disabled={loading || importing}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
                onClick={() => loadFromUrl(sourceUrl)}
                disabled={!sourceUrl.trim() || loading || importing}
              >
                Laden
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || importing}
              >
                Datei wählen
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                hidden
                onChange={handleFile}
              />
            </Stack>
          </Grid>
        </Grid>

        {serviceLayers.length > 1 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Ebene dieses Dienstes</InputLabel>
            <Select
              label="Ebene dieses Dienstes"
              value={serviceLayers.some(layer => layer.id === activeLayerId) ? activeLayerId : ''}
              onChange={(event) => loadFromUrl(`${serviceUrl}/${event.target.value}`)}
              disabled={loading || importing}
            >
              {serviceLayers.map(layer => (
                <MenuItem key={layer.id} value={layer.id}>{layer.name}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Derselbe Dienst führt oft mehrere Gebietseinteilungen — Stadtteile für Sektoren, Gemeindegrenzen für das
              Betriebsgebiet.
            </Typography>
          </FormControl>
        )}

        </>
        )}

        {loadHints.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Adresse ergänzt: {loadHints.join(', ')}.
          </Alert>
        )}

        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}

        {features.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ display: sourceKind === 'search' ? 'none' : undefined }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Spalte für den Namen</InputLabel>
                  <Select
                    label="Spalte für den Namen"
                    value={propertyKeys.includes(nameKey) ? nameKey : ''}
                    onChange={(event) => setNameKey(event.target.value)}
                    disabled={importing}
                  >
                    {propertyKeys.map(key => <MenuItem key={key} value={key}>{key}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ display: isServiceAreaMode || sourceKind === 'search' ? 'none' : undefined }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Spalte für die Sektornummer</InputLabel>
                  <Select
                    label="Spalte für die Sektornummer"
                    value={propertyKeys.includes(codeKey) ? codeKey : ''}
                    onChange={(event) => setCodeKey(event.target.value)}
                    disabled={importing}
                  >
                    <MenuItem value="">— keine —</MenuItem>
                    {propertyKeys.map(key => <MenuItem key={key} value={key}>{key}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Genauigkeit</InputLabel>
                  <Select
                    label="Genauigkeit"
                    value={simplifyKey}
                    onChange={(event) => setSimplifyKey(event.target.value)}
                    disabled={importing}
                  >
                    {SIMPLIFY_LEVELS.map(level => (
                      <MenuItem key={level.key} value={level.key}>{level.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mb: 2 }}>
              Amtliche Grenzen haben mehrere hundert Stützpunkte. „Mittel" genügt für die Sektor-Zuordnung und hält die
              Flächen im Karteneditor bearbeitbar. Benachbarte Sektoren können durch das Vereinfachen an ihrer
              gemeinsamen Grenze um wenige Meter auseinanderdriften — wer das vermeiden will, wählt „Fein".
            </Alert>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <TableContainer sx={{ maxHeight: 380 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedRows.length > 0 && selectedRows.length < importableCount}
                            checked={importableCount > 0 && selectedRows.length === importableCount}
                            onChange={(event) => toggleAll(event.target.checked)}
                            disabled={importing}
                          />
                        </TableCell>
                        <TableCell>Name</TableCell>
                        {!isServiceAreaMode && <TableCell sx={{ width: 110 }}>Nummer</TableCell>}
                        <TableCell>Punkte</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => {
                        const candidate = candidateByKey[row.key];
                        if (!candidate) return null;
                        const blockReason = candidateBlockReason(candidate);
                        const error = rowErrors[row.key];
                        return (
                          <TableRow key={row.key} hover>
                            <TableCell padding="checkbox">
                              <Tooltip title={blockReason || ''} disableHoverListener={!blockReason}>
                                <span>
                                  <Checkbox
                                    checked={row.selected}
                                    disabled={Boolean(blockReason) || importing}
                                    onChange={(event) => updateRow(row.key, { selected: event.target.checked })}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {isServiceAreaMode ? (
                                <Typography variant="body2">{row.name || '(ohne Namen)'}</Typography>
                              ) : (
                                <TextField
                                  fullWidth
                                  variant="standard"
                                  value={row.name}
                                  error={error === 'Name fehlt'}
                                  disabled={Boolean(blockReason) || importing}
                                  onChange={(event) => updateRow(row.key, { name: event.target.value })}
                                />
                              )}
                              {sourceKind === 'search' && (
                                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                  {[candidate.properties?.art, candidate.properties?.adresse].filter(Boolean).join(' · ')}
                                </Typography>
                              )}
                              {blockReason && (
                                <Typography variant="caption" color="text.secondary">{blockReason}</Typography>
                              )}
                              {error && error !== 'Name fehlt' && (
                                <Typography variant="caption" color="error">{error}</Typography>
                              )}
                            </TableCell>
                            {!isServiceAreaMode && (
                              <TableCell>
                                <TextField
                                  fullWidth
                                  variant="standard"
                                  value={row.code}
                                  error={Boolean(error) && error !== 'Name fehlt'}
                                  disabled={Boolean(blockReason) || importing}
                                  onChange={(event) => updateRow(row.key, { code: event.target.value })}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {candidate.rawPointCount} → <strong>{candidate.pointCount}</strong>
                              </Typography>
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                {candidate.skippedRingCount > 0 && (
                                  <Tooltip title="Getrennte Teilflächen (Exklaven). Übernommen wird die größte; die übrigen können bei Bedarf als eigener Sektor nachgezeichnet werden.">
                                    <Chip size="small" color="warning" variant="outlined" label={`+${candidate.skippedRingCount} Teilfl.`} />
                                  </Tooltip>
                                )}
                                {candidate.holeCount > 0 && (
                                  <Tooltip title="Die Fläche enthält Löcher. Sektoren können keine Löcher abbilden — der Außenrand wird übernommen.">
                                    <Chip size="small" color="warning" variant="outlined" label={`${candidate.holeCount} Loch`} />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                {isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={MAP_STYLE}
                    center={FALLBACK_CENTER}
                    zoom={10}
                    onLoad={(map) => { mapRef.current = map; fitPreview(map); }}
                    onUnmount={() => { mapRef.current = null; }}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                  >
                    {previewPolygons.map((candidate, index) => (
                      <Polygon
                        key={candidate.key}
                        paths={candidate.points}
                        options={{
                          fillColor: colorForIndex(index),
                          fillOpacity: 0.2,
                          strokeColor: colorForIndex(index),
                          strokeOpacity: 0.9,
                          strokeWeight: 2,
                          clickable: false
                        }}
                      />
                    ))}
                  </GoogleMap>
                ) : (
                  <Alert severity="info">Karte wird geladen…</Alert>
                )}

                {isServiceAreaMode && serviceAreaCandidate && (
                  <Box sx={{ mt: 1 }}>
                    {serviceAreaCandidate.unionError ? (
                      <Alert severity="error">{serviceAreaCandidate.unionError}</Alert>
                    ) : serviceAreaBlockReason ? (
                      <Alert severity="warning">{serviceAreaBlockReason}</Alert>
                    ) : (
                      <Alert severity="success">
                        {selectedRows.length > 1
                          ? `${selectedRows.length} Flächen vereinigt — Außenriss mit ${serviceAreaCandidate.pointCount} Punkten.`
                          : `Eine Fläche mit ${serviceAreaCandidate.pointCount} Punkten.`}
                        {serviceAreaCandidate.skippedRingCount > 0 && ` Achtung: ${serviceAreaCandidate.skippedRingCount} getrennte Teilfläche(n) liegen außerhalb und werden nicht übernommen.`}
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
          {importing
            ? `${importProgress} von ${selectedRows.length} angelegt…`
            : features.length === 0
              ? ''
              : isServiceAreaMode
                ? (selectedRows.length
                    ? `${selectedRows.length} von ${importableCount} Flächen gewählt`
                    : `${importableCount} Flächen — bitte auswählen`)
                : `${selectedRows.length} von ${importableCount} Flächen ausgewählt`}
        </Typography>
        <Button onClick={handleClose} disabled={importing}>Abbrechen</Button>
        {isServiceAreaMode ? (
          <Button
            variant="contained"
            onClick={handleTakeServiceArea}
            disabled={!serviceAreaCandidate || Boolean(serviceAreaCandidate.unionError) || Boolean(serviceAreaBlockReason)}
          >
            In den Betriebsgebiet-Editor übernehmen
          </Button>
        ) : (
          <Tooltip title={hasErrors ? 'Bitte zuerst die markierten Namen und Nummern korrigieren' : ''} disableHoverListener={!hasErrors}>
            <span>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={!selectedRows.length || hasErrors || importing}
              >
                {selectedRows.length > 1 ? `${selectedRows.length} Sektoren anlegen` : 'Sektor anlegen'}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SectorImportDialog;
